export interface NotebookTemplateCell {
    id: string;
    type: 'code' | 'markdown';
    content: string;
}

export interface NotebookTemplate {
    id: string;
    title: string;
    description: string;
    tags: string[];
    cells: NotebookTemplateCell[];
}

export const NOTEBOOK_TEMPLATES: NotebookTemplate[] = [
    {
        id: 'quickstart',
        title: '🚀 Python Interactive Basics',
        description: 'Learn interactive Python execution, variable persistence between cells, and basic algorithms.',
        tags: ['Basics', 'Zero VPS Load', 'Python 3.12'],
        cells: [
            {
                id: 'cell-md-1',
                type: 'markdown',
                content: `# 🚀 Welcome to the AI Fellowship Interactive Python Lab
This notebook environment runs **100% inside your browser** using **WebAssembly (Pyodide)**.
- ⚡ **Zero VPS Load**: All computations run safely in your browser.
- 🔄 **Stateful Kernel**: Variables and functions defined in earlier cells persist across cells below.
- ⌨️ **Shortcuts**: Press \`Shift + Enter\` to run a cell and advance, or \`Ctrl + Enter\` to run in place.`,
            },
            {
                id: 'cell-code-1',
                type: 'code',
                content: `# Cell 1: Define variables and greeting
user_name = "AI Fellow"
batch = 2026
skills = ["Python", "Pandas", "PyTorch", "Prompt Engineering"]

print(f"Hello, {user_name}! Welcome to Fellowship Batch {batch}.")
print(f"Loaded skills: {', '.join(skills)}")`,
            },
            {
                id: 'cell-md-2',
                type: 'markdown',
                content: `### Cross-Cell Variable Sharing
Notice how \`skills\` and \`user_name\` from the previous cell are immediately accessible below!`,
            },
            {
                id: 'cell-code-2',
                type: 'code',
                content: `# Cell 2: List comprehension using variables from Cell 1
uppercase_skills = [s.upper() for s in skills]

# Returning an expression automatically displays its output
uppercase_skills`,
            },
            {
                id: 'cell-code-3',
                type: 'code',
                content: `# Cell 3: Fast prime number generator function
def get_primes(limit):
    primes = []
    for num in range(2, limit + 1):
        if all(num % i != 0 for i in range(2, int(num**0.5) + 1)):
            primes.append(num)
    return primes

primes_up_to_50 = get_primes(50)
print(f"Primes up to 50: {primes_up_to_50}")
print(f"Total count: {len(primes_up_to_50)}")`,
            },
        ],
    },
    {
        id: 'pandas-numpy',
        title: '📊 Data Analytics with Pandas & NumPy',
        description: 'Explore dataset creation, statistical analysis, grouping, and formatted HTML DataFrames.',
        tags: ['Pandas', 'NumPy', 'Data Science'],
        cells: [
            {
                id: 'p-md-1',
                type: 'markdown',
                content: `# 📊 Data Analysis with NumPy & Pandas
Pyodide automatically downloads and loads **NumPy** and **Pandas** directly in WebAssembly!`,
            },
            {
                id: 'p-code-1',
                type: 'code',
                content: `import numpy as np
import pandas as pd

# Generate reproducible synthetic student cohort performance data
np.random.seed(42)
n_students = 10

data = {
    'Student ID': [f"STU-{100 + i}" for i in range(n_students)],
    'Name': ['Alice', 'Bob', 'Charlie', 'Diana', 'Evan', 'Fiona', 'George', 'Hannah', 'Ian', 'Julia'],
    'Track': np.random.choice(['Computer Vision', 'NLP & LLMs', 'Full Stack AI'], n_students),
    'Assignments Completed': np.random.randint(6, 11, n_students),
    'Assessment Score (%)': np.random.randint(72, 99, n_students),
    'Attendance (%)': np.random.randint(85, 100, n_students),
}

df = pd.DataFrame(data)
# Evaluating DataFrame renders an interactive formatted table!
df`,
            },
            {
                id: 'p-md-2',
                type: 'markdown',
                content: `### Track-wise Aggregation & Performance Summary`,
            },
            {
                id: 'p-code-2',
                type: 'code',
                content: `# Group by Track and compute mean scores
track_summary = df.groupby('Track').agg(
    Total_Students=('Student ID', 'count'),
    Avg_Score=('Assessment Score (%)', 'mean'),
    Avg_Attendance=('Attendance (%)', 'mean'),
    Max_Score=('Assessment Score (%)', 'max')
).round(2).reset_index()

track_summary`,
            },
            {
                id: 'p-code-3',
                type: 'code',
                content: `# Filter top performers with score >= 90
top_performers = df[df['Assessment Score (%)'] >= 90].sort_values(by='Assessment Score (%)', ascending=False)
print(f"Found {len(top_performers)} students scoring 90%+:")
top_performers[['Name', 'Track', 'Assessment Score (%)', 'Attendance (%)']]`,
            },
        ],
    },
    {
        id: 'matplotlib-viz',
        title: '📈 Matplotlib Visualizations & Plots',
        description: 'Generate high-resolution inline charts, sine curves, histograms, and bar graphs.',
        tags: ['Matplotlib', 'Visualizations', 'Charts'],
        cells: [
            {
                id: 'm-md-1',
                type: 'markdown',
                content: `# 📈 Matplotlib Inline Visualizations
Any \`plt.figure()\` or \`plt.plot()\` call is automatically intercepted and rendered as a high-definition inline image.`,
            },
            {
                id: 'm-code-1',
                type: 'code',
                content: `import matplotlib.pyplot as plt
import numpy as np

# Create smooth trigonometric signals
x = np.linspace(0, 4 * np.pi, 200)
y_sin = np.sin(x)
y_cos = np.cos(x)

fig, ax = plt.subplots(figsize=(8, 4), dpi=120)
ax.plot(x, y_sin, label='Sine Wave (sin x)', color='#3b82f6', linewidth=2.5)
ax.plot(x, y_cos, label='Cosine Wave (cos x)', color='#ef4444', linewidth=2, linestyle='--')

ax.set_title('Trigonometric Signal Waveforms', fontsize=14, fontweight='bold', pad=12)
ax.set_xlabel('Angle (Radians)', fontsize=11)
ax.set_ylabel('Amplitude', fontsize=11)
ax.grid(True, linestyle=':', alpha=0.6)
ax.legend(frameon=True, facecolor='white', framealpha=0.9)
plt.tight_layout()
plt.show()`,
            },
            {
                id: 'm-code-2',
                type: 'code',
                content: `# Bar Chart: AI Fellowship Module Completion Rate
modules = ['Python Core', 'Data Prep', 'ML Basics', 'Deep Learning', 'LLM Agents']
completion_rates = [96, 91, 88, 82, 79]
colors = ['#10b981', '#06b6d4', '#6366f1', '#8b5cf6', '#ec4899']

fig, ax = plt.subplots(figsize=(8, 4.2), dpi=120)
bars = ax.bar(modules, completion_rates, color=colors, width=0.55, edgecolor='#1e293b', linewidth=0.8)

# Add value labels on top of bars
for bar in bars:
    height = bar.get_height()
    ax.text(bar.get_x() + bar.get_width()/2., height + 1.2, f'{height}%', ha='center', va='bottom', fontweight='bold', fontsize=10)

ax.set_ylim(0, 110)
ax.set_title('Cohort Module Completion Breakdown', fontsize=13, fontweight='bold')
ax.set_ylabel('Completion Rate (%)', fontsize=11)
ax.grid(axis='y', linestyle='--', alpha=0.5)
plt.tight_layout()
plt.show()`,
            },
        ],
    },
    {
        id: 'machine-learning',
        title: '🤖 Scikit-Learn Machine Learning',
        description: 'Train a regression model and evaluate performance metrics inside the browser.',
        tags: ['Scikit-Learn', 'Machine Learning', 'Linear Regression'],
        cells: [
            {
                id: 'ml-md-1',
                type: 'markdown',
                content: `# 🤖 Machine Learning with Scikit-Learn
Train a Linear Regression model in browser WebAssembly to predict student outcomes based on study hours and mock test scores.`,
            },
            {
                id: 'ml-code-1',
                type: 'code',
                content: `from sklearn.linear_model import LinearRegression
from sklearn.metrics import mean_squared_error, r2_score
import numpy as np
import pandas as pd

# Training data: [Hours Studied, Practice Questions Solved]
X = np.array([
    [2, 10], [3, 15], [4, 25], [5, 30], [6, 45],
    [7, 50], [8, 65], [9, 75], [10, 90], [12, 110]
])

# Target: Final Exam Score (out of 100)
y = np.array([55, 60, 68, 74, 80, 85, 89, 93, 97, 99])

# Train model
model = LinearRegression()
model.fit(X, y)

y_pred = model.predict(X)
r2 = r2_score(y, y_pred)
mse = mean_squared_error(y, y_pred)

print(f"Model Trained Successfully!")
print(f"R² Score (Accuracy): {r2:.4f}")
print(f"Mean Squared Error: {mse:.4f}")
print(f"Formula: Final Score ≈ {model.intercept_:.2f} + ({model.coef_[0]:.2f} * Hours) + ({model.coef_[1]:.2f} * Questions)")`,
            },
            {
                id: 'ml-code-2',
                type: 'code',
                content: `# Make interactive predictions for new student scenarios
new_students = np.array([
    [5, 40],   # Student A: 5 hours, 40 practice questions
    [8, 80],   # Student B: 8 hours, 80 practice questions
    [11, 100]  # Student C: 11 hours, 100 practice questions
])

predicted_scores = model.predict(new_students)
results = pd.DataFrame({
    'Scenario': ['Student A', 'Student B', 'Student C'],
    'Study Hours': new_students[:, 0],
    'Questions Solved': new_students[:, 1],
    'Predicted Score (%)': np.clip(predicted_scores, 0, 100).round(1)
})

results`,
            },
        ],
    },
];
